import { useEffect } from "react";
import { Amplify } from "aws-amplify";
import { configureAutoTrack, flushEvents } from "aws-amplify/analytics";
import { Authenticator } from "@aws-amplify/ui-react";
import {
  createBrowserRouter,
  RouterProvider,
  Outlet,
  ScrollRestoration,
} from "react-router-dom";
import { MantineProvider } from "@mantine/core";
import { Userpilot } from "userpilot";
import Settings from "@/pages/Settings";
import ClaimableAuthenticator from "@/components/shared/ClaimableAuthenticator";
import IdentifyUser from "@/components/IdentifyUser";
import PageViewTracker from "@/components/PageViewTracker";
import mantineTheme from "@/mantineTheme";

import "./App.scss";
import "@aws-amplify/ui-react/styles.css";
import "@mantine/core/styles.css";
import "@mantine/dates/styles.css";
import HomePage from "@/pages/HomePage/HomePage";
import ReferPatient from "@/pages/ReferPatient/ReferPatient";
import SponsorAppeals from "@/pages/SponsorAppeals";
import { UserProviderSetupWrapperWithFeatureFlag } from "./components/UserProviderSetupWrapper/UserProviderSetupWrapper";

Amplify.configure({
  Auth: {
    Cognito: {
      userPoolId: import.meta.env.VITE_COGNITO_USER_POOL_ID,
      userPoolClientId: import.meta.env.VITE_COGNITO_USER_POOL_CLIENT_ID,
      identityPoolId: import.meta.env.VITE_IDENTITY_POOL_ID,
      allowGuestAccess: true,
    },
  },
  Analytics: {
    Pinpoint: {
      appId: import.meta.env.VITE_PINPOINT_APP_ID,
      region: "us-east-1",
    },
  },
});

configureAutoTrack({
  enable: true,
  type: "session",
});

configureAutoTrack({
  enable: true,
  type: "pageView",
  options: {
    appType: "singlePage",
  },
});

Userpilot.initialize("NX-32bc6362");

function App() {
  const router = createBrowserRouter([
    {
      element: (
        <ClaimableAuthenticator>
          <UserProviderSetupWrapperWithFeatureFlag>
            <ScrollRestoration />
            <PageViewTracker />
            <Outlet />
          </UserProviderSetupWrapperWithFeatureFlag>
        </ClaimableAuthenticator>
      ),
      children: [
        {
          path: "/",
          element: <HomePage />,
        },
        { path: "/refer-patient", element: <ReferPatient /> },
        { path: "/settings", element: <Settings /> },
        { path: "/sponsor-appeals", element: <SponsorAppeals /> },
      ],
    },
  ]);

  useEffect(() => {
    const handleBeforeUnload = () => {
      flushEvents();
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, []);

  return (
    <MantineProvider theme={mantineTheme}>
      <Authenticator.Provider>
        <RouterProvider router={router} />
        <IdentifyUser />
      </Authenticator.Provider>
    </MantineProvider>
  );
}

export default App;
